import type { InventoryItem, StoredUrl } from "@/services/api-inventory/apiInventory.types";

const URLS_STORAGE_KEY = "urls";
const DEFAULT_API_URLS: StoredUrl[] = ["http://localhost:8000"];

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeInventoryItem(value: unknown): InventoryItem | null {
  if (!isRecord(value)) {
    return null;
  }

  const name = normalizeString(value.name);
  const path = normalizeString(value.path);

  if (!name || !path) {
    return null;
  }

  const contact = isRecord(value.contact) ? value.contact : {};

  return {
    name,
    description: normalizeString(value.description),
    path,
    contact: {
      name: normalizeString(contact.name),
      url: normalizeString(contact.url),
      email: normalizeString(contact.email),
    },
  };
}

function normalizeStoredUrl(value: unknown): StoredUrl | null {
  const url = normalizeString(value);

  if (url) {
    return url;
  }

  return normalizeInventoryItem(value);
}

function normalizeStoredUrls(value: unknown): StoredUrl[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  return value.reduce<StoredUrl[]>((urls, item) => {
    const normalizedUrl = normalizeStoredUrl(item);

    if (normalizedUrl) {
      urls.push(normalizedUrl);
    }

    return urls;
  }, []);
}

export function constructUrl(baseUrl: string, path: string): string {
  try {
    return new URL(path).toString();
  } catch {
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    const normalizedPath = path.replace(/^\/+/, "");

    return new URL(normalizedPath, normalizedBaseUrl).toString();
  }
}

export function appendQuery(url: string, query: Record<string, unknown>): string {
  const urlObject = new URL(url);

  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      urlObject.searchParams.set(key, String(value));
    }
  });

  return urlObject.toString();
}

export function readStoredUrls(): StoredUrl[] {
  try {
    const storedUrls = localStorage.getItem(URLS_STORAGE_KEY);

    if (storedUrls === null) {
      return [...DEFAULT_API_URLS];
    }

    const parsed: unknown = JSON.parse(storedUrls);
    return normalizeStoredUrls(parsed) ?? [...DEFAULT_API_URLS];
  } catch {
    return [...DEFAULT_API_URLS];
  }
}

export function writeStoredUrls(urls: StoredUrl[]): StoredUrl[] {
  const normalizedUrls = normalizeStoredUrls(urls) ?? [];

  localStorage.setItem(URLS_STORAGE_KEY, JSON.stringify(normalizedUrls));

  return normalizedUrls;
}

export function validateAndFilterAddresses(inputRanges: string[]) {
  return inputRanges
    ? inputRanges.filter((address) => {
        const excelAddressPattern = /^(?:[a-zA-Z0-9_]+!)?[A-Z]+[0-9]+(?::[A-Z]+[0-9]+)?$/;
        return excelAddressPattern.test(address);
      })
    : [];
}

export function standardizeDecimalSeparator(value: unknown): unknown {
  // Handle matrices (2D arrays)
  if (Array.isArray(value)) {
    // Check if it's a matrix (array of arrays)
    if (Array.isArray(value[0])) {
      // Process each row in the matrix
      const rows = value as unknown[][];
      return rows.map((row) => row.map((cell) => standardizeDecimalSeparator(cell)));
    } else {
      // Process each element in a 1D array
      return value.map((item) => standardizeDecimalSeparator(item));
    }
  }

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    const commaDecimalPattern = /^-?[\d\s]+,\d+$/;
    const europeFormatPattern = /^-?(\d{1,3}(\.\d{3})+),\d+$/;
    const plainNumberPattern = /^-?\d+(\.\d+)?$/;

    if (commaDecimalPattern.test(trimmedValue)) {
      return parseFloat(trimmedValue.replace(",", "."));
    } else if (europeFormatPattern.test(trimmedValue)) {
      return parseFloat(trimmedValue.replace(/\./g, "").replace(",", "."));
    } else if (plainNumberPattern.test(trimmedValue)) {
      return parseFloat(trimmedValue);
    }
  }

  return value;
}
